import { Task, TaskCardRef } from 'src/app/models/task/task.model';
import { PendingApproval, PendingCardCopy } from 'src/app/services/approvals-inbox/approvals-inbox.service';
import { MessageRouteContext, MessageRouteTarget, resolveMessageRoute } from 'src/app/components/global-search/global-search.utils';
import { taskCardRoute, taskWaitingCard } from 'src/app/utils/task-cards';

/**
 * One thing waiting on the person — the rail's Dashboard badge counts these and
 * the dashboard's "Needs you" list shows them (Vincent, 2026-11-03: "Yes, count
 * them"): every open card of the agent-approvals inbox, whether or not a task
 * is behind it, plus the open tasks that need the person and are not already
 * one of those cards (a funds hold, a tool or credential request — message-only
 * cards — or a card outside the inbox).
 */
export type NeedsYouItem =
  | { type: 'card'; key: string; card: PendingApproval }
  | { type: 'task'; key: string; task: Task; card: TaskCardRef | null };

const copyKey = (c: { conversationId: string; messageId: string }): string => `${c.conversationId}/${c.messageId}`;

/**
 * Cards first (newest first, as the inbox orders them), then the tasks. Counted
 * once each:
 *  - a task whose `waitingOnCard` is an inbox card is that card (by approval id,
 *    or by any of its copies — the task may point at the space copy or the 1:1
 *    copy of the same approval);
 *  - an inbox record is one card, whatever its copies;
 *  - tasks waiting on the same card are one item.
 * `tasksNeedingYou` are already the open tasks that need the person
 * (TasksService.needsYou). Pure.
 */
export function needsYouItems(cards: readonly PendingApproval[], tasksNeedingYou: readonly Task[]): NeedsYouItem[] {
  const items: NeedsYouItem[] = cards.map((card) => ({ type: 'card', key: `card:${card.id}`, card }));
  const approvalIds = new Set(cards.map((card) => card.id));
  const copies = new Set(cards.flatMap((card) => card.copies.map(copyKey)));
  const seen = new Set<string>();
  for (const task of tasksNeedingYou) {
    const card = taskWaitingCard(task);
    if (card && ((card.approvalId && approvalIds.has(card.approvalId)) || copies.has(copyKey(card)))) continue;
    const key = card ? (card.approvalId ? `card:${card.approvalId}` : `message:${copyKey(card)}`) : `task:${task.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ type: 'task', key, task, card });
  }
  return items;
}

/** What NeedsYou routes by: the user's team / space conversations, and every conversation they are known to be in. */
export interface NeedsYouRouteContext extends MessageRouteContext {
  /** Team spaces, spaces and agent chats in the user's lists: a copy there is one they can open. */
  knownConversationIds: ReadonlySet<string>;
}

/** The copy of a card the person opens: the first one in a conversation they are known to be in, else the primary. */
export function cardCopyFor(card: PendingApproval, known: ReadonlySet<string>): PendingCardCopy | null {
  return card.copies.find((c) => known.has(c.conversationId)) ?? card.copies[0] ?? null;
}

/**
 * Where an item opens: a card in its conversation, scrolled to the message (team
 * spaces under /messaging, agent chats under /agents); a task's card the same
 * way; a task waiting on no card (out of credits) on the board.
 */
export function needsYouRoute(item: NeedsYouItem, context: NeedsYouRouteContext): MessageRouteTarget {
  if (item.type === 'task') {
    return item.card ? taskCardRoute(item.card, context) : { commands: ['/tasks'], queryParams: {} };
  }
  const { card } = item;
  const copy = cardCopyFor(card, context.knownConversationIds);
  if (!copy) {
    // A record without its message ids: its conversation at least.
    const route = card.conversationCategory === 'channel' || context.teamConversationIds.has(card.conversationId)
      || context.spaceConversationIds.has(card.conversationId) ? '/messaging' : '/agents';
    return { commands: [route], queryParams: { conversationId: card.conversationId } };
  }
  // Gate and plan records stamp the primary copy's category: a team space opens under /messaging even before the
  // teams list has loaded.
  const primaryIsSpace = card.conversationCategory === 'channel' && copy.conversationId === card.conversationId;
  return resolveMessageRoute(copy, {
    teamConversationIds: primaryIsSpace ? new Set([...context.teamConversationIds, copy.conversationId]) : context.teamConversationIds,
    spaceConversationIds: context.spaceConversationIds,
  });
}
